import { App, markRaw } from "vue";
import { createPinia, Pinia } from "pinia";
import { createPersistedState } from "pinia-plugin-persistedstate";
import Container from "./di/DependencyContainer.ts";
import AuthRepositoryMock from "../repositories/mocks/AuthRepositoryMock.ts";
import SessionRepositoryMock from "../repositories/mocks/SessionRepositoryMock.ts";
import AuthRepository from "../repositories/implementations/AuthRepository.ts";
import SessionRepository from "../repositories/implementations/SessionRepository.ts";
import initializeI18n from "./initializeI18n.ts";
import getRouter from "../router";
import LedgerRepositoryMock from "../repositories/mocks/LedgerRepositoryMock.ts";

import Vue3Toastify, { type ToastContainerOptions } from "vue3-toastify";
import UserRepositoryMock from "../repositories/mocks/UserRepositoryMock.ts";
import { Router } from "vue-router";
import setAxiosDefaults from "../libs/core/axios.ts";
import ConfigService from "@/services/ConfigService.ts";
import UserRepository from "@/repositories/implementations/UserRepository.ts";
import { usePersonStore } from "@/store";
import LedgerRepository from "@/repositories/implementations/LedgerRepository.ts";

/**
 * @function initializeServices
 * Initializes and mounts the service singletons
 * Services must load in the following order: config, auth, then app.
 * @param {Function} [next=undefined] Optional callback function
 */
async function initializeServices(): Promise<void> {
  const config = await ConfigService.init();

  if (config.isDevelopment()) {
    Container.register("authRepository", new AuthRepositoryMock());
    Container.register("sessionRepository", new SessionRepositoryMock());
    Container.register("ledgerRepository", new LedgerRepositoryMock());
    Container.register("userRepository", new UserRepositoryMock());
  } else {
    Container.register("authRepository", new AuthRepository());
    Container.register("sessionRepository", new SessionRepository());
    Container.register("ledgerRepository", new LedgerRepository());
    Container.register("userRepository", new UserRepository());
  }
}

export default async function initializeApp(app: App): Promise<{ router: Router, store: Pinia }> {
  setAxiosDefaults();
  await initializeServices();

  const store = createPinia();

  store.use(
    createPersistedState({
      key: (id) => `rootapi.${id}`
    })
  );

  app.use(store);

  const personStore = usePersonStore();
  await personStore.getState();


  const router = getRouter();

  store.use(({ store }) => {
    store.router = markRaw(router);
  });

  app.use(router);

  initializeI18n(app);

  app.use(Vue3Toastify, {
    autoClose: 3000
  } as ToastContainerOptions);

  return { router, store };
}
